/**
 * The Road cargo section's values <-> the neutral road cargo block. Road is one
 * of three shipment types (less than truckload, parcel, full truckload); each
 * accepts only its own fields. Weights are kilograms and sizes centimetres,
 * like the other modes.
 */
import type {
  LtlCargoRequest,
  RoadCargoRequest,
} from '../../../apis/lanePlanner';

export type RoadType = 'ltl' | 'parcel' | 'ftl';

export const ROAD_TYPE_OPTIONS: Array<{ value: RoadType; label: string }> = [
  { value: 'ltl', label: 'Less than truckload' },
  { value: 'parcel', label: 'Parcel' },
  { value: 'ftl', label: 'Full truckload' },
];

export const EQUIPMENT_OPTIONS = [
  { value: 'Dry van', label: 'Dry van' },
  { value: 'Reefer', label: 'Reefer' },
];

/** Temperature ranges of a reefer truck (IATA-style codes, as for air). */
export const ROAD_TEMP_OPTIONS = [
  { value: 'COL', label: '2–8°C' },
  { value: 'CRT', label: '15–25°C' },
  { value: 'ERT', label: '2–25°C' },
  { value: 'FRO', label: '−20 to −10°C' },
];

export const PARCEL_SERVICE_OPTIONS = [
  { value: 'Priority overnight', label: 'Priority overnight' },
  { value: 'Standard overnight', label: 'Standard overnight' },
  { value: '2-day', label: '2-day' },
  { value: 'Ground', label: 'Ground' },
];

/** The accessorials offered first, then the rest behind "More accessorials". */
export const COMMON_ACCESSORIALS = [
  { value: 'liftgate_delivery', label: 'Liftgate at delivery' },
  { value: 'residential_delivery', label: 'Residential delivery' },
  { value: 'delivery_appointment', label: 'Delivery appointment' },
];
export const MORE_ACCESSORIALS = [
  { value: 'liftgate_pickup', label: 'Liftgate at pickup' },
  { value: 'residential_pickup', label: 'Residential pickup' },
  { value: 'inside_delivery', label: 'Inside delivery' },
  { value: 'inside_pickup', label: 'Inside pickup' },
  { value: 'limited_access_delivery', label: 'Limited access delivery' },
  { value: 'limited_access_pickup', label: 'Limited access pickup' },
  { value: 'notify_before_delivery', label: 'Notify before delivery' },
];

/** NMFC freight classes, lightest-density last. */
export const FREIGHT_CLASSES = [
  '50',
  '55',
  '60',
  '65',
  '70',
  '77.5',
  '85',
  '92.5',
  '100',
  '110',
  '125',
  '150',
  '175',
  '200',
  '250',
  '300',
  '400',
  '500',
];

export interface RoadCargoValues {
  roadType: RoadType;
  equipment?: string;
  tempRange?: string;
  pallets?: number | string;
  weightPerPallet?: number | string;
  packages?: number | string;
  weightPerPackage?: number | string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  freightClass?: string;
  nmfcCode?: string;
  accessorials?: string[];
  parcelService?: string;
  residential?: boolean;
  teamDrivers?: boolean;
  droppedTrailer?: boolean;
}

export const defaultRoadCargoValues = (): RoadCargoValues => ({
  roadType: 'ltl',
  equipment: 'Dry van',
  pallets: 1,
  packages: 1,
  accessorials: [],
  residential: false,
  teamDrivers: false,
  droppedTrailer: false,
});

const num = (value: number | string | undefined): number | null => {
  const n = Number(value);
  return value === undefined || value === '' || !Number.isFinite(n) ? null : n;
};

const dimensions = (values: RoadCargoValues) => ({
  length_cm: num(values.length),
  width_cm: num(values.width),
  height_cm: num(values.height),
});

/** Less-than-truckload cargo from the section values, whatever `roadType` they carry. */
export const buildLtlCargo = (values: RoadCargoValues): LtlCargoRequest => ({
  type: 'ltl',
  pallets: Number(values.pallets),
  weight_per_pallet_kg: Number(values.weightPerPallet),
  equipment: values.equipment || null,
  freight_class: values.freightClass || null,
  nmfc_code: values.nmfcCode?.trim() || null,
  accessorials: values.accessorials ?? [],
  ...dimensions(values),
});

/** Validated section values -> the `road` block of the search request. */
export const buildRoadCargo = (values: RoadCargoValues): RoadCargoRequest => {
  if (values.roadType === 'parcel') {
    return {
      type: 'parcel',
      packages: Number(values.packages),
      weight_per_package_kg: Number(values.weightPerPackage),
      parcel_service: values.parcelService || null,
      residential: !!values.residential,
      ...dimensions(values),
    };
  }
  if (values.roadType === 'ftl') {
    const reefer = values.equipment === 'Reefer';
    return {
      type: 'ftl',
      equipment: values.equipment || 'Dry van',
      temp_range: reefer ? values.tempRange || null : null,
      team_drivers: !!values.teamDrivers,
      dropped_trailer: !!values.droppedTrailer,
    };
  }
  return buildLtlCargo(values);
};

/** Reverse of `buildRoadCargo`: a stored `road` block -> section values. */
export const roadCargoValuesFromRequest = (
  road: RoadCargoRequest
): RoadCargoValues => {
  const defaults = defaultRoadCargoValues();
  if (road.type === 'parcel') {
    return {
      ...defaults,
      roadType: 'parcel',
      packages: road.packages,
      weightPerPackage: road.weight_per_package_kg,
      parcelService: road.parcel_service ?? undefined,
      residential: !!road.residential,
      length: road.length_cm ?? undefined,
      width: road.width_cm ?? undefined,
      height: road.height_cm ?? undefined,
    };
  }
  if (road.type === 'ftl') {
    return {
      ...defaults,
      roadType: 'ftl',
      equipment: road.equipment,
      tempRange: road.temp_range ?? undefined,
      teamDrivers: !!road.team_drivers,
      droppedTrailer: !!road.dropped_trailer,
    };
  }
  return {
    ...defaults,
    roadType: 'ltl',
    pallets: road.pallets,
    weightPerPallet: road.weight_per_pallet_kg,
    equipment: road.equipment ?? defaults.equipment,
    freightClass: road.freight_class ?? undefined,
    nmfcCode: road.nmfc_code ?? undefined,
    accessorials: road.accessorials ?? [],
    length: road.length_cm ?? undefined,
    width: road.width_cm ?? undefined,
    height: road.height_cm ?? undefined,
  };
};

// ---------------------------------------------------------------------------
// Summaries shown under the fields (estimates only; the vendor prices the freight)
// ---------------------------------------------------------------------------

const LB_PER_KG = 2.20462;
const CM3_PER_FT3 = 28316.85;
/** Dimensional-weight divisor for parcels, cm^3 per kg (139 in^3 per lb). */
const PARCEL_DIM_DIVISOR = 5000;

/** NMFC freight class suggested by density (lb per cubic foot). */
export const estimateFreightClass = (densityLbFt3: number): string => {
  const table: Array<[number, string]> = [
    [50, '50'],
    [35, '55'],
    [30, '60'],
    [22.5, '65'],
    [15, '70'],
    [13.5, '77.5'],
    [12, '85'],
    [10.5, '92.5'],
    [9, '100'],
    [8, '110'],
    [7, '125'],
    [6, '150'],
    [5, '175'],
    [4, '200'],
    [3, '250'],
    [2, '300'],
    [1, '400'],
  ];
  return table.find(([min]) => densityLbFt3 >= min)?.[1] ?? '500';
};

export interface LtlSummary {
  totalWeightLb: number;
  totalVolumeFt3: number;
  densityLbFt3: number;
  estimatedClass: string;
}

/** Totals for the whole shipment (pallets x per-pallet figures); null until enough is entered. */
export const ltlSummary = (values: RoadCargoValues): LtlSummary | null => {
  const pallets = num(values.pallets);
  const weight = num(values.weightPerPallet);
  const [l, w, h] = [values.length, values.width, values.height].map(num);
  if (!pallets || !weight || !l || !w || !h) return null;
  const totalWeightLb = pallets * weight * LB_PER_KG;
  const totalVolumeFt3 = (pallets * l * w * h) / CM3_PER_FT3;
  const densityLbFt3 = totalWeightLb / totalVolumeFt3;
  return {
    totalWeightLb,
    totalVolumeFt3,
    densityLbFt3,
    estimatedClass: estimateFreightClass(densityLbFt3),
  };
};

export interface ParcelSummary {
  actualKg: number;
  dimKg: number;
  billableKg: number;
}

/** Actual, dimensional and billable weight per package; null until enough is entered. */
export const parcelSummary = (
  values: RoadCargoValues
): ParcelSummary | null => {
  const weight = num(values.weightPerPackage);
  const [l, w, h] = [values.length, values.width, values.height].map(num);
  if (!weight || !l || !w || !h) return null;
  const dimKg = (l * w * h) / PARCEL_DIM_DIVISOR;
  return {
    actualKg: weight,
    dimKg,
    billableKg: Math.max(weight, dimKg),
  };
};
